import {
  Box,
  Button,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getCourseLevelLabel } from "../../../constants/courseLevels";
import { fetchCourseCatalog } from "../../../redux/Actions/courseActions";
import {
  clearCreateCourseState,
  createCourse,
  fetchCreatedCourses,
} from "../../../redux/Actions/courseCreateActions";
import { fetchCourseTree } from "../../../redux/Actions/courseTreeAction";
import MessageCard from "../../MessageCard/MessageCard";
import TeacherNavbar from "./TeacherNavbar";

// const courseTypes = ["Normal", "Intensive"];
const weekdays = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const CreateCourse = () => {
  const { t } = useTranslation();
  const [parentTitle, setParentTitle] = useState("");
  const [courseType, setCourseType] = useState("Normal");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [audienceType, setAudienceType] = useState("");
  const [minimumAge, setMinimumAge] = useState("");
  const [maximumAge, setMaximumAge] = useState("");
  const [dayTimes, setDayTimes] = useState([
    { dayOfWeek: "", startTime: "", endTime: "" },
  ]);
  const [showMessage, setShowMessage] = useState(false);
  const [tableExpanded, setTableExpanded] = useState(false);
  const [localMessage, setLocalMessage] = useState(null);
  const [localMessageType, setLocalMessageType] = useState("info");

  const dispatch = useDispatch();

  const userId = useSelector((state) => state.auth.user.userId);
  const profileName = useSelector((state) => state.auth.user.profileName);
  const roleName = useSelector((state) => state.auth.user.roleName);

  const {
    loading: createLoading,
    success,
    message,
    error: createError,
  } = useSelector((state) => state.createCourse || {});

  const {
    loading: createdCoursesLoading,
    createdCourses,
    error: createdCoursesError,
  } = useSelector((state) => state.createCourse || {});

  const { treeData } = useSelector((state) => state.courseTree);
  const displayError = (value) =>
    typeof value === "string" ? value : value?.message || JSON.stringify(value || "");

  const parentTitles = treeData.map((course) => course.title);

  const handleSubmit = () => {
    setLocalMessage(null);
    if (
      !parentTitle ||
      dayTimes.length === 0
    ) {
      setLocalMessage("Please fill in all fields.");
      setLocalMessageType("error");
      return;
    }

    const timeToMinutes = (value) => {
      const [h, m] = value.split(":").map(Number);
      return h * 60 + m;
    };
    const daySet = new Set();
    for (const dt of dayTimes) {
      if (!dt.dayOfWeek) {
        setLocalMessage("Please select a day for each schedule entry.");
        setLocalMessageType("error");
        return;
      }
      if (daySet.has(dt.dayOfWeek)) {
        setLocalMessage("Duplicate days are not allowed in the schedule.");
        setLocalMessageType("error");
        return;
      }
      daySet.add(dt.dayOfWeek);
      if (!dt.startTime || !dt.endTime) {
        setLocalMessage(
          `Please set start and end time for ${dt.dayOfWeek}.`
        );
        setLocalMessageType("error");
        return;
      }
      if (timeToMinutes(dt.endTime) <= timeToMinutes(dt.startTime)) {
        setLocalMessage(
          `End time must be after start time for ${dt.dayOfWeek}.`
        );
        setLocalMessageType("error");
        return;
      }
    }

    const normalizedDayTimes = dayTimes.map((dt) => ({
      dayOfWeek: dt.dayOfWeek,
      startTime: dt.startTime,
      endTime: dt.endTime,
    }));
    const daysPerWeek = normalizedDayTimes.length;
    const startTime = normalizedDayTimes.reduce((min, dt) => {
      return timeToMinutes(dt.startTime) < timeToMinutes(min)
        ? dt.startTime
        : min;
    }, normalizedDayTimes[0].startTime);
    const endTime = normalizedDayTimes.reduce((max, dt) => {
      return timeToMinutes(dt.endTime) > timeToMinutes(max)
        ? dt.endTime
        : max;
    }, normalizedDayTimes[0].endTime);

    if ((minimumAge === "") !== (maximumAge === "")) {
      setLocalMessage("Enter both minimum and maximum age, or leave both empty.");
      setLocalMessageType("error");
      return;
    }
    if (minimumAge !== "" && (Number(minimumAge) < 0 || Number(maximumAge) > 120 || Number(minimumAge) > Number(maximumAge))) {
      setLocalMessage("Enter a valid age range between 0 and 120.");
      setLocalMessageType("error");
      return;
    }

    const courseData = {
      userId,
      courseTitle: parentTitle,
      parentTitle,
      courseType,
      daysPerWeek,
      startDate,
      endDate,
      startTime,
      endTime,
      audienceType: audienceType || null,
      minimumAge: minimumAge === "" ? null : Number(minimumAge),
      maximumAge: maximumAge === "" ? null : Number(maximumAge),
      dayTimes: normalizedDayTimes,
    };

    dispatch(createCourse(courseData));
    setShowMessage(true);
  };

  const handleDayTimeChange = (index, field, value) => {
    setDayTimes((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddDayTime = () => {
    setDayTimes((prev) => [
      ...prev,
      { dayOfWeek: "", startTime: "", endTime: "" },
    ]);
  };

  const handleRemoveDayTime = (index) => {
    setDayTimes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFetchCreatedCourses = () => {
    dispatch(fetchCreatedCourses(userId));
    setTableExpanded(true);
  };

  useEffect(() => {
    dispatch(fetchCourseCatalog());
    dispatch(fetchCourseTree());
  }, [dispatch]);

  useEffect(() => {
    if (!success) return;
    setParentTitle("");
    setCourseType("Normal");
    setStartDate("");
    setEndDate("");
    setAudienceType("");
    setMinimumAge("");
    setMaximumAge("");
    setDayTimes([{ dayOfWeek: "", startTime: "", endTime: "" }]);
    setLocalMessage(null);
    setLocalMessageType("info");
  }, [success]);

  const handleCloseMessage = () => {
    setShowMessage(false);
    dispatch(clearCreateCourseState());
  };

  const toggleTable = () => {
    if (!tableExpanded) {
      handleFetchCreatedCourses();
    }
    setTableExpanded(!tableExpanded);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <TeacherNavbar profileName={profileName} roleName={roleName} />
      <Box
        sx={{
          ml: { xs: 0, md: "250px" },
          px: 14,
          py: 3,
          width: { xs: "100%", md: "calc(100% - 250px)" },
          boxSizing: "border-box",
          bgcolor: "#f0f7ff",
          height: "100vh",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            bgcolor: "white",
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            mx: "auto",
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{ color: "#0077b6", mb: 3, fontWeight: "bold" }}
          >
            Create Course
          </Typography>

          <Grid container spacing={3}>
            {/* --- Row 1: Course Identity --- */}
            {/* Parent Course */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Course Level"
                value={parentTitle}
                onChange={(e) => {
                  setParentTitle(e.target.value);
                }}
              >
                {parentTitles.map((title, index) => (
                  <MenuItem key={index} value={title}>
                    {getCourseLevelLabel(t, title)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Course Type (Hidden) */}
            {/* <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Course Type"
                value={courseType}
                onChange={(e) => setCourseType(e.target.value)}
              >
                {courseTypes.map((option, index) => (
                  <MenuItem key={index} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid> */}

            {/* --- Row 2: Duration (Dates) --- */}
            {/* Start Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Optional target audience and age range */}
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Audience (optional)" value={audienceType} onChange={(e) => setAudienceType(e.target.value)}>
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="ADULTS">Adults</MenuItem>
                <MenuItem value="CHILDREN">Children</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Minimum age (optional)" type="number" value={minimumAge} onChange={(e) => setMinimumAge(e.target.value)} inputProps={{ min: 0, max: 120 }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Maximum age (optional)" type="number" value={maximumAge} onChange={(e) => setMaximumAge(e.target.value)} inputProps={{ min: 0, max: 120 }} />
            </Grid>

            {/* --- Row 3: Schedule (Day + Time) --- */}
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                Schedule (per day)
              </Typography>
            </Grid>
            {dayTimes.map((dt, index) => (
              <Grid item xs={12} key={`${dt.dayOfWeek}-${index}`}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      fullWidth
                      label="Day"
                      value={dt.dayOfWeek}
                      onChange={(e) =>
                        handleDayTimeChange(index, "dayOfWeek", e.target.value)
                      }
                    >
                      {weekdays.map((day) => (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      label="Start Time"
                      type="time"
                      value={dt.startTime}
                      onChange={(e) =>
                        handleDayTimeChange(index, "startTime", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      label="End Time"
                      type="time"
                      value={dt.endTime}
                      onChange={(e) =>
                        handleDayTimeChange(index, "endTime", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleRemoveDayTime(index)}
                      disabled={dayTimes.length === 1}
                      sx={{ width: "100%" }}
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Button variant="outlined" onClick={handleAddDayTime}>
                Add Day
              </Button>
            </Grid>
          </Grid>

          {/* Create Button */}
          <Box mt={3} display="flex" justifyContent="center">
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                !parentTitle ||
                dayTimes.length === 0 ||
                createLoading
              }
            >
              Create Course
            </Button>
          </Box>

          {/* Message */}
          {localMessage && (
            <MessageCard
              message={localMessage}
              type={localMessageType}
              onClose={() => setLocalMessage(null)}
            />
          )}
          {showMessage && message && (
            <MessageCard
              message={message}
              type={success ? "success" : "error"}
              onClose={handleCloseMessage}
            />
          )}

          {createLoading && <Typography>Submitting course...</Typography>}

          {/* Show Created Courses */}
          <Box mt={3} display="flex" justifyContent="center">
            <Button variant="contained" color="secondary" onClick={toggleTable}>
              {tableExpanded ? "Hide Created Courses" : "Show Created Courses"}
            </Button>
          </Box>

          {tableExpanded && (
            <>
              {createdCoursesLoading && (
                <Typography>Loading created courses...</Typography>
              )}
              {createdCoursesError && (
                <Typography>Error: {displayError(createdCoursesError)}</Typography>
              )}

              {createdCourses && createdCourses.length > 0 && (
                <Table sx={{ mt: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Course Title</TableCell>
                      <TableCell>Course Level</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Schedule</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {createdCourses.map((createdCourse, index) => (
                      <TableRow key={index}>
                        <TableCell>{createdCourse.title}</TableCell>
                        <TableCell>{createdCourse.parentTitle || createdCourse.title}</TableCell>
                        <TableCell>
                          {new Date(createdCourse.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {createdCourse.dayTimes?.length
                            ? createdCourse.dayTimes
                                .map(
                                  (dt) =>
                                    `${dt.dayOfWeek} ${dt.startTime}-${dt.endTime}`
                                )
                                .join(", ")
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
          {createError && <Typography>Error: {displayError(createError)}</Typography>}
        </Box>
      </Box>
    </Box>
  );
};

export default CreateCourse;

